/**
 * What is happening to the client's documents right now, drawn under SectionIndex's filters, which
 * is where a person is already looking after the Add document modal closes.
 *
 * Expects the files the Add document modal is sending, how many documents the api is still reading,
 * the documents whose reading finished while the page was open, and the two callbacks that send the
 * page to a document's row and drop every finished line.
 *
 * Renders three things in order: one line for the files being sent, one line for the documents
 * being read, and one line per finished document, each a button that sends the page to that
 * document's row in the Documents table. It renders nothing at all when all three are empty, so the
 * left column keeps its height until something starts.
 */

import type { TaxDocumentListItem } from '@shared/types/taxProfile';
import { Spinner } from '@/components/Spinner';
import type { AddDocumentChoice } from '@/pages/TaxProfile/components/AddDocumentModal';
import {
  addInFlightSentence,
  documentOutcomeLine,
  readingSentence,
} from '@/pages/TaxProfile/wording';

export interface DocumentActivityProps {
  /** The files the Add document modal is sending right now. Null when no add is in flight. */
  add: { count: number; choice: AddDocumentChoice } | null;
  /** How many documents the api is still reading, counted off the documents list the page polls. */
  readingCount: number;
  /** The documents whose reading finished while the page was open, oldest first. */
  finished: TaxDocumentListItem[];
  /** Sends the page to that document's row in the Documents table. */
  onShowDocument: (documentId: number) => void;
  /** Drops every finished line. */
  onDismiss: () => void;
}

export default function DocumentActivity({
  add,
  readingCount,
  finished,
  onShowDocument,
  onDismiss,
}: DocumentActivityProps) {
  if (add === null && readingCount === 0 && finished.length === 0) return null;

  return (
    <>
      <div className='divider my-2' />
      <div className='text-xs' aria-live='polite' data-testid='tax-activity'>
        <div className='flex items-start justify-between gap-2 mb-1'>
          <h4 className='text-xs font-bold uppercase opacity-70'>Activity</h4>
          {finished.length > 0 && (
            <button
              type='button'
              className='btn btn-ghost btn-xs px-1 min-h-0 h-5'
              aria-label='Dismiss'
              onClick={onDismiss}
            >
              ×
            </button>
          )}
        </div>

        {add !== null && (
          <p className='flex items-center gap-1 py-0.5'>
            <Spinner className='w-3 h-3 shrink-0' />
            {addInFlightSentence(add.count, add.choice)}
          </p>
        )}

        {readingCount > 0 && (
          <p className='flex items-center gap-1 py-0.5'>
            <Spinner className='w-3 h-3 shrink-0' />
            {readingSentence(readingCount)}
          </p>
        )}

        {finished.map((document) => (
          <button
            key={document.id}
            type='button'
            className='btn btn-link btn-xs px-0 min-h-0 h-auto py-0.5 normal-case
              font-normal text-left justify-start whitespace-normal leading-snug'
            onClick={() => onShowDocument(document.id)}
          >
            {documentOutcomeLine(document)}
          </button>
        ))}
      </div>
    </>
  );
}
